import LazyImage from 'components/Image/LazyImage';
import noImage from 'components/Image/noIMG.svg';
import { mapProductToAiMatched } from 'components/QuickOrder/ChangeProductModal';
import { formatVND } from 'containers/QuickOrder/utils';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import { AiMatchedProduct } from 'types/schema';
import { SearchIcon } from '../icons';
import messages from '../messages';
import { useProductSearch } from '../useProductSearch';

interface InlineSearchProps {
  onSelect: (alt: AiMatchedProduct) => void;
  onClose: () => void;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #111827;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #bdbdbd;
  }
`;

const Dropdown = styled.div<{ top: number; left: number }>`
  position: fixed;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: 320px;
  background: #fff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  z-index: 1002;
  max-height: 242px;
  overflow-y: auto;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f5f5;
  }
`;

const Thumb = styled(({ noImage: _noImage, ...rest }) => <LazyImage {...rest} />)<{ noImage: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  object-fit: ${({ noImage: noImg }) => (noImg ? 'contain' : 'cover')};
  ${({ noImage: noImg }) => (noImg ? 'opacity: 0.3;' : '')}
  flex-shrink: 0;
  background: #f5f5f5;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Unit = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #9e9e9e;
  margin-top: 2px;
`;

const Price = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #333333;
  flex-shrink: 0;
`;

const InlineSearch = ({ onSelect, onClose }: InlineSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { results, searchProducts, clearResults } = useProductSearch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setSearchQuery('');
    clearResults();
    onClose();
  }, [clearResults, onClose]);

  const getDropdownPos = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    return { top: rect ? rect.bottom + 6 : 0, left: rect ? rect.left : 0 };
  }, []);

  const [dropdownPos, setDropdownPos] = useState(getDropdownPos);

  useEffect(() => {
    const update = () => setDropdownPos(getDropdownPos());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [getDropdownPos]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const inWrapper = wrapperRef.current?.contains(e.target as Node);
      const inDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!inWrapper && !inDropdown) close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [close]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSearchQuery(val);
      searchProducts(val);
    },
    [searchProducts],
  );

  const handleSelect = useCallback(
    (r) => {
      onSelect(mapProductToAiMatched(r));
      close();
    },
    [onSelect, close],
  );

  return (
    <Wrapper ref={wrapperRef}>
      <Bar>
        <SearchIcon />
        <Input
          autoFocus
          placeholder={translations(messages.searchPlaceholder)}
          value={searchQuery}
          onChange={handleChange}
        />
      </Bar>
      {results.length > 0 && (
        <Dropdown ref={dropdownRef} top={dropdownPos.top} left={dropdownPos.left}>
          {results.map((r) => (
            <Row key={r.id} type="button" onClick={() => handleSelect(r)}>
              <Thumb noImage={!r.imageUrl} src={r.imageUrl || noImage} alt={r.name ?? ''} />
              <Info>
                <Name>{r.name}</Name>
                <Unit>{r.uomLocal || r.uom}</Unit>
              </Info>
              <Price>{formatVND(r.priceV2?.price || 0)}</Price>
            </Row>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default InlineSearch;
